import {useState} from 'react'
import {Link} from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import {useQuery, useDebounce, useQuote} from 'hooks'
import {queryExplore, querySearch} from 'graph'
import {LoadingPage, LoadingEllipsis} from 'common/Loading'
import Avatar from 'common/Avatar'

function PhotoCard({post}: any) {
    const foto = post.fotos?.[0]
    if (!foto?.image?.uri) {
        return null
    }
    return (
        <Link
            to={`/adventures/${post.adventure.id}/post/${post.id}`}
            className="explore-tile"
            style={{
                position: 'relative',
                aspectRatio: '1 / 1',
                overflow: 'hidden',
                borderRadius: 8,
                display: 'block',
            }}
            title={post.adventure.title}
        >
            <img
                src={foto.image.uri}
                alt={post.adventure.title}
                loading="lazy"
                style={{width: '100%', height: '100%', objectFit: 'cover'}}
            />
        </Link>
    )
}

function ExploreGrid() {
    const {data, loading, fetchMore} = useQuery(queryExplore, {
        notifyOnNetworkStatusChange: true,
        variables: {offset: 0, limit: 24},
    })

    if (loading && !data) {
        return <LoadingPage />
    }

    const edges = data?.explore?.edges ?? []
    const total = data?.explore?.pagination?.total ?? 0

    if (edges.length === 0) {
        return <p className="text-muted text-center my-5">No suggestions yet.</p>
    }

    return (
        <InfiniteScroll
            dataLength={edges.length}
            next={() => fetchMore({variables: {offset: edges.length}})}
            hasMore={total > edges.length}
            loader={<LoadingEllipsis loading={true} />}
            style={{overflow: 'visible'}}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 6,
                }}
            >
                {edges.map((post: any) => (
                    <PhotoCard key={post.id} post={post} />
                ))}
            </div>
        </InfiniteScroll>
    )
}

function SearchResults({keyword}: {keyword: string}) {
    const {data, loading} = useQuery(querySearch, {variables: {keyword}})

    if (loading && !data) {
        return <LoadingPage />
    }

    const adventures = data?.search?.adventures ?? []
    const users = data?.search?.users ?? []

    if (adventures.length === 0 && users.length === 0) {
        return <p className="text-muted text-center my-5">No results for “{keyword}”.</p>
    }

    return (
        <>
            {users.length > 0 && (
                <div className="mb-4">
                    <h2 className="h6 text-uppercase text-muted mb-3">People</h2>
                    {users.map((user: any) => (
                        <Link
                            key={user.id}
                            to={`/users/${user.username}`}
                            className="d-flex align-items-center text-decoration-none text-body py-2"
                        >
                            <Avatar user={user} />
                            <div className="ms-3">
                                <div className="fw-semibold">{user.fullname}</div>
                                <div className="text-muted small">@{user.username}</div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
            {adventures.length > 0 && (
                <div>
                    <h2 className="h6 text-uppercase text-muted mb-3">Adventures</h2>
                    <div className="row g-3">
                        {adventures.map((adventure: any) => (
                            <div className="col-12 col-md-6" key={adventure.id}>
                                <Link
                                    to={`/adventures/${adventure.id}`}
                                    className="card h-100 text-decoration-none text-body"
                                >
                                    <div className="card-body">
                                        <div className="fw-semibold">{adventure.title}</div>
                                        {adventure.description && (
                                            <div className="text-muted small text-truncate">
                                                {adventure.description}
                                            </div>
                                        )}
                                        <div className="text-muted small mt-1">
                                            by {adventure.author?.fullname}
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    )
}

export default function Explore() {
    const [keyword, setKeyword] = useState('')
    const debounced = useDebounce(keyword, 300)
    const searching = debounced.trim().length >= 2
    const quote = useQuote('explore')

    return (
        <div className="container" style={{maxWidth: 720}}>
            <div className="row text-center my-4">
                <div className="col">
                    <h1 className="display-3">
                        Explore
                        <br />
                        <span className="display-4 text-muted fw-lighter">
                            {quote.text ?? 'Discover people and adventures'}
                        </span>
                    </h1>
                    {quote.source && (
                        <p className="text-muted fst-italic mb-0">— {quote.source}</p>
                    )}
                </div>
            </div>
            <div className="mb-4">
                <input
                    type="search"
                    className="form-control form-control-lg"
                    placeholder="Search people and adventures…"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    autoComplete="off"
                />
            </div>
            {searching ? <SearchResults keyword={debounced.trim()} /> : <ExploreGrid />}
        </div>
    )
}
